import { useTranslation } from 'react-i18next'
import { ChevronUpIcon } from '../../icons/ChevronUpIcon'
import { ChevronDownIcon } from '../../icons/ChevronDownIcon'

/* ─── Sortable column heading (spec §15a) ───

   One glyph in every state — a single chevron, never a double arrow. When the
   column is the active sort it points at the current direction and is drawn in the
   accent on a light accent shape; otherwise it is dimmed, has no shape, and points
   at the direction the next click will apply.

   That last part is what makes the control readable: an inactive chevron is a
   promise about what clicking does, not a claim about the current state. */

export type SortDirection = 'asc' | 'desc'

export interface SortState {
  col: string
  dir: SortDirection
}

export interface SortableThProps {
  /** The sort key sent to the API, e.g. `channel`. */
  colKey: string
  /** The localised heading. */
  label: string
  sort: SortState
  onSort: (colKey: string) => void
  /** Columns that read newest-first when first clicked — the timestamp column. */
  defaultsToDescending?: boolean
  /** Optional CSS width for the column, e.g. `'11%'`. Without it the table
   *  auto-lays out and column widths drift as the content changes between pages. */
  width?: string
}

export function SortableTh({
  colKey,
  label,
  sort,
  onSort,
  defaultsToDescending = false,
  width,
}: Readonly<SortableThProps>) {
  const { t } = useTranslation()
  const isActive = sort.col === colKey
  const nextDir: SortDirection = isActive
    ? sort.dir === 'asc'
      ? 'desc'
      : 'asc'
    : defaultsToDescending
      ? 'desc'
      : 'asc'
  const shown = isActive ? sort.dir : nextDir
  const sortedDirection = sort.dir === 'asc' ? 'ascending' : 'descending'
  const ariaSort = isActive ? sortedDirection : 'none'

  return (
    // `aria-sort` is a property of the *column*, so it belongs on the header cell.
    // On the button it describes the control rather than the column, and screen
    // readers announcing the table's sorted column never reach it.
    <th aria-sort={ariaSort} style={width ? { width } : undefined}>
      <span className="th-wrap">
        {label}
        <button
          type="button"
          className={isActive ? 'th-sort on' : 'th-sort'}
          title={nextDir === 'asc' ? t('mcpAnalytics.detail.sortAscending') : t('mcpAnalytics.detail.sortDescending')}
          aria-label={t('mcpAnalytics.detail.sortBy', { column: label })}
          onClick={() => onSort(colKey)}
        >
          {shown === 'asc' ? (
            <ChevronUpIcon size={12} strokeWidth={2.4} />
          ) : (
            <ChevronDownIcon size={12} strokeWidth={2.4} />
          )}
        </button>
      </span>
    </th>
  )
}

/** Advance a sort state by one click on `colKey`: flip the direction if it is
 *  already the sorted column, otherwise adopt that column's own default. */
export function toggleSort(
  sort: SortState,
  colKey: string,
  defaultsToDescending: boolean,
): SortState {
  if (sort.col === colKey) return { col: colKey, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
  return { col: colKey, dir: defaultsToDescending ? 'desc' : 'asc' }
}
